/**
 * WHERE YOU LEFT OFF (LOGIN-TO-TENANT.md §8a, owner 2026-10-23) — the room (agentic or expert) or network a person last
 * had open, per workspace. Recorded on CHANGE only (debounced) to ``PUT /auth/last-place``; the server returns the places
 * that still open (``lastPlaces`` on ``/auth/workspaces`` — membership + the room/network re-checked), and the post-login
 * home / a workspace switch opens it. Only the place — never a dialog, a draft or an action. A deep link always wins
 * (this is used only where the app would otherwise pick a home).
 */
import { getRestApiBearerToken } from '../services/apiToken.js';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';
const DEBOUNCE_MS = 1500;

let _serverPlaces = {};             // {tenantId: place} — validated by the server at the last /auth/workspaces
const _sent = {};                   // {tenantId: key of the last place written}
const _timers = {};               // one debounce per workspace — a quick switch never drops the other's write

export function placeFromLocation(pathname, search) {
  const path = String(pathname || '');
  if (path === '/agentic') {
    const q = new URLSearchParams(search || '');
    const networkId = (q.get('networkId') || '').trim();
    const poolId = (q.get('poolId') || '').trim();
    return networkId && poolId ? { kind: 'room', networkId, poolId } : null;
  }
  const m = /^\/network\/([^/?#]+)/.exec(path);
  if (m && m[1] !== 'new') return { kind: 'network', networkId: decodeURIComponent(m[1]) };
  return null;
}

export function pathOfPlace(place) {
  if (!place) return '';
  if (place.kind === 'room' && place.networkId && place.poolId) {
    return `/agentic?networkId=${encodeURIComponent(place.networkId)}&poolId=${encodeURIComponent(place.poolId)}`;
  }
  if (place.kind === 'network' && place.networkId) return `/network/${encodeURIComponent(place.networkId)}`;
  return '';
}

const keyOf = (p) => (p ? `${p.kind}|${p.networkId}|${p.poolId || ''}` : '');

export function setServerPlaces(places) {
  _serverPlaces = places && typeof places === 'object' ? { ...places } : {};
  for (const [t, p] of Object.entries(_serverPlaces)) _sent[t] = keyOf(p);
}

/** The home to open for ``tenantId``: its remembered place (a network only for those who may use the workbench), else
 *  ``fallback``. */
export function homeFor(tenantId, { canWorkbench = false, fallback = '' } = {}) {
  const p = _serverPlaces[String(tenantId || '')];
  if (!p || (p.kind === 'network' && !canWorkbench)) return fallback;
  return pathOfPlace(p) || fallback;
}

/** Record the current place — written only when it CHANGED for that workspace, at most once per DEBOUNCE_MS. */
export function recordPlace(tenantId, place, { fetchImpl = (typeof fetch !== 'undefined' ? fetch : null) } = {}) {
  const tid = String(tenantId || '');
  if (!tid || !place || !fetchImpl) return false;
  const k = keyOf(place);
  if (_sent[tid] === k) return false;
  _sent[tid] = k;
  _serverPlaces[tid] = place;
  if (_timers[tid]) clearTimeout(_timers[tid]);
  _timers[tid] = setTimeout(() => {
    delete _timers[tid];
    const token = getRestApiBearerToken();
    if (!token) return;
    fetchImpl(`${API_BASE_URL}/auth/last-place`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ tenantId: tid, place }),
    }).catch(() => {});
  }, DEBOUNCE_MS);
  return true;
}

export function _resetForTests() {
  _serverPlaces = {};
  for (const k of Object.keys(_sent)) delete _sent[k];
  for (const k of Object.keys(_timers)) { clearTimeout(_timers[k]); delete _timers[k]; }
}
